import { useMemo } from 'react';
import { skipToken, useQuery } from '@tanstack/react-query';
import { scheduleKey } from '@constants/queryKeys';
import type { ISchedulerData } from '@interfaces/IScheduler';
import { isScheduleDirty } from '../lib/unsavedPlanChanges';
import { useSchedulerDirty, useSchedulerSite } from './useSchedulerStore';

/**
 * THE "unsaved changes" answer for a loaded schedule — one derivation read by
 * the toolbar's Save, the leave-page and switch-schedule prompts, and both
 * commit reviews' "Save your changes first". The store's sticky edit flag says
 * an edit happened; it does not un-set on Undo, so on its own it blocked a
 * commit while Save sat disabled. Dirty means the working copy differs from
 * the last saved copy (the schedule query's cache entry, which every save and
 * reload keeps current).
 *
 * Reads the cache entry without fetching (`skipToken`): the page that owns the
 * schedule fetches it, this only follows it.
 */
export function useScheduleDirty(siteId: string, scheduleNumber: string | undefined): boolean {
  const flagged = useSchedulerDirty(siteId);
  const working = useSchedulerSite(siteId);
  const { data: saved } = useQuery<ISchedulerData>({ queryKey: scheduleKey(siteId, scheduleNumber), queryFn: skipToken });
  return useMemo(() => isScheduleDirty(flagged, saved, working), [flagged, saved, working]);
}
